import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { ManufacturingPricingDetailModal } from './ManufacturingPricingDetailModal'
import {
  useManufacturingPricing,
  type ManufacturingPricingSummaryDto
} from '../../../viewmodels/useManufacturingPricing'

/**
 * /manufacturing/pricing — list + drill-down.
 *
 * List shows one row per active raw material; the "Supplier pricing"
 * column lists each supplier's name + price (stacked rows when a
 * material is priced by more than one supplier). The Action opens the
 * detail modal to add / edit / remove supplier prices.
 */
export function ManufacturingPricingPage(): JSX.Element {
  const vm = useManufacturingPricing()
  const [detailTarget, setDetailTarget] = useState<ManufacturingPricingSummaryDto | null>(null)

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Manufacturing Pricing</h1>
        <div className="text-body-sm text-text-secondary">
          One raw material may be priced by multiple suppliers. Click a row to
          add, edit, or remove supplier prices.
        </div>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table items={vm.items} onOpen={setDetailTarget} />
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}

      <ManufacturingPricingDetailModal
        target={detailTarget}
        onClose={() => setDetailTarget(null)}
        fetchDetail={vm.fetchDetail}
        addPricing={vm.addPricing}
        updatePricing={vm.updatePricing}
        deletePricing={vm.deletePricing}
      />
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No raw materials yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Add raw materials on the{' '}
        <span className="font-medium">Raw Materials</span> screen first, then come
        back here to price them per supplier.
      </p>
    </div>
  )
}

function formatPaisa(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

function Table({
  items,
  onOpen
}: {
  readonly items: ManufacturingPricingSummaryDto[]
  readonly onOpen: (row: ManufacturingPricingSummaryDto) => void
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Material</Th>
            <Th>Unit</Th>
            <Th>Supplier pricing</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.raw_material_id} className="border-t border-border align-top">
              <Td>{row.raw_material_name}</Td>
              <Td><span className="font-mono text-body-xs">{row.raw_material_unit_label}</span></Td>
              <Td>
                {row.supplier_prices.length === 0 ? (
                  <span className="text-text-secondary">No supplier prices yet</span>
                ) : (
                  <div className="flex flex-col divide-y divide-[color:var(--color-border-surface)]">
                    {row.supplier_prices.map((sp) => (
                      <div key={sp.supplier_id} className="flex items-baseline justify-between gap-3 py-1.5 first:pt-0 last:pb-0">
                        <span className="text-text-primary">{sp.supplier_name}</span>
                        <span className="font-mono whitespace-nowrap">
                          {sp.discounted_price_paisa !== null ? (
                            <>
                              {formatPaisa(sp.discounted_price_paisa)}
                              <span className="ml-1 text-text-secondary line-through">
                                {formatPaisa(sp.seller_price_paisa)}
                              </span>
                            </>
                          ) : (
                            formatPaisa(sp.seller_price_paisa)
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Td>
              <Td className="text-right whitespace-nowrap">
                <Button size="sm" variant="secondary" onClick={() => onOpen(row)}>
                  {row.supplier_prices.length === 0 ? 'Add pricing' : 'Manage'}
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
